/* eslint-disable max-lines */
import {
  useDeleteUser,
  useUpdateUser,
  useValidRoles,
  useTenants,
} from '@hakimo-ui/hakimo/data-access';
import { QueryResult, MultiSelect } from '@hakimo-ui/hakimo/ui-elements';
import { toast, useUser } from '@hakimo-ui/hakimo/util';
import {
  Alert,
  Button,
  InputField,
  Modal,
  Radio,
  Label,
} from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import { Tenant } from '@hakimo-ui/hakimo/types';

interface Props {
  currentUserData: {
    id: string;
    role: string;
    email: string;
    employee_id?: string;
    tenant: { id: string; name: string };
    mspTenants: { id: string; name: string }[] | null;
  };
  onClose: () => void;
}

export function EditUserModal(props: Props) {
  const { currentUserData, onClose } = props;
  const {
    id: userId,
    role: currentUserRole,
    email,
    employee_id: currentEmployeeId,
    tenant,
    mspTenants,
  } = currentUserData;

  const [role, setRole] = useState<string>(currentUserRole);
  const [employeeId, setEmployeeId] = useState<string>(currentEmployeeId || '');

  const loggedInUser = useUser();

  const currentUserTenants = [
    { id: tenant.id, name: tenant.name },
    ...(mspTenants ? mspTenants.map((t) => ({ id: t.id, name: t.name })) : []),
  ].filter(
    (t, index, array) => array.findIndex((item) => item.id === t.id) === index
  );

  const [selectedTenants, setSelectedTenants] =
    useState<Tenant[]>(currentUserTenants);
  const roleQuery = useValidRoles();
  const tenantsQuery = useTenants();

  const allLoggedInUserTenants = [
    loggedInUser.tenant,
    ...(loggedInUser.mspTenants || []),
  ].filter(
    (t, index, array) => array.findIndex((item) => item.id === t.id) === index
  );

  const totalAvailableTenants = allLoggedInUserTenants.length;
  const shouldHideTenantFields = totalAvailableTenants <= 1;

  const hasChanges = Boolean(
    String(currentUserRole) !== String(role) ||
      employeeId !== (currentEmployeeId || '') ||
      (!shouldHideTenantFields &&
        JSON.stringify(selectedTenants.map((t) => t.id).sort()) !==
          JSON.stringify(currentUserTenants.map((t) => t.id).sort()))
  );

  const onCloseCb = () => {
    onClose();
    updateUserMutation.reset();
  };
  const updateUserMutation = useUpdateUser(userId, () => {
    onCloseCb();
    toast('User updated', { type: 'success' });
  });
  const deleteUserMutation = useDeleteUser(userId, () => {
    onCloseCb();
    toast('User deleted', { type: 'success' });
  });

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={() => {
          const effectiveTenants = shouldHideTenantFields
            ? currentUserTenants
            : selectedTenants;

          const primaryTenant = effectiveTenants[0];

          if (primaryTenant) {
            const allMspTenants = effectiveTenants.map((t) => t.id);

            updateUserMutation.mutate({
              roles: [role],
              employee_id: employeeId || undefined,
              tenant_id: primaryTenant.id,
              msp_tenants: allMspTenants.join(','),
            });
          }
        }}
        disabled={
          !hasChanges ||
          !role ||
          (shouldHideTenantFields ? false : selectedTenants.length === 0)
        }
        loading={deleteUserMutation.isLoading || updateUserMutation.isLoading}
      >
        Submit
      </Button>
      <Button onClick={onCloseCb}>Cancel</Button>
    </>
  );

  return (
    <Modal title="Edit User" open={true} onClose={onCloseCb} footer={actions}>
      <div className="w-[40rem] max-w-6xl">
        {updateUserMutation.isError ? (
          <div className="px-8 py-4">
            <Alert type="error">{updateUserMutation.error.message}</Alert>
          </div>
        ) : null}
        {deleteUserMutation.isError ? (
          <div className="px-8 py-4">
            <Alert type="error">{deleteUserMutation.error.message}</Alert>
          </div>
        ) : null}
        <div className="mb-4 px-8 py-4">
          <InputField
            onChange={(e) => setEmployeeId(e.target.value)}
            label="Employee ID"
            type="text"
            value={employeeId}
          />
        </div>
        {!shouldHideTenantFields && (
          <div className="mb-4 px-8 py-4">
            <QueryResult queryResult={tenantsQuery}>
              {(data) => (
                <div>
                  <label className="dark:text-dark-text mb-1 block text-xs text-gray-700">
                    Tenants
                  </label>
                  <div className="relative mb-2">
                    <MultiSelect
                      getItems={(query: string) =>
                        data.tenants.filter(
                          (t) =>
                            t.name
                              .toLowerCase()
                              .includes(query.toLowerCase()) &&
                            !selectedTenants.some(
                              (selected) => selected.id === t.id
                            )
                        )
                      }
                      displayValue={(item) => item?.name ?? ''}
                      onChange={setSelectedTenants}
                      value={selectedTenants}
                      customSelectedRenderer={() => <div />}
                    />
                  </div>
                  {selectedTenants.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedTenants.map((it, idx) => {
                        const isBaseTenant = it.id === tenant.id;
                        return (
                          <span key={it.id}>
                            <Label
                              text={it.name}
                              removable={!isBaseTenant}
                              onClickRemove={() =>
                                !isBaseTenant &&
                                setSelectedTenants([
                                  ...selectedTenants.slice(0, idx),
                                  ...selectedTenants.slice(idx + 1),
                                ])
                              }
                            />
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </QueryResult>
          </div>
        )}

        <div className="mb-2 px-8 py-4">
          <QueryResult queryResult={roleQuery}>
            {(data) => (
              <div className="max-h-56 overflow-y-auto rounded-md border border-gray-600 p-2">
                <Radio
                  items={data ?? []}
                  value={role}
                  onChange={setRole}
                  displayValue={(item) => item}
                />
              </div>
            )}
          </QueryResult>
        </div>

        <div className="mb-2 px-8 py-4">
          <Button
            variant="error"
            onClick={() => deleteUserMutation.mutate({ enabled: false })}
            loading={
              deleteUserMutation.isLoading || updateUserMutation.isLoading
            }
          >
            Delete User {email}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default EditUserModal;
